import ErrorManager from '@/common/utils/ErrorManager';
import { ERROR } from '@/modules/ErrorToaster/store/mutation-types';
import { RootState } from '@/plugins/store';
import { ActionTree } from 'vuex';
import DemandeInterventionService, { FilterDemandeIntervention } from '../services/DemandeInterventionService';
import { DemandeInterventionActions, DemandeInterventionActionTypes } from './action-types';
import { DEMANDE_INTERVENTION } from './mutation-types';
import { SuiviDemandeInterventionState } from './state';
import NavFilter from '@/models/NavFilter';
import axios from 'axios';
import { EQUIPEMENT } from '@/modules/Equipements/store/mutation-types';

let abortController: AbortController | null = null;

export const actions: ActionTree<SuiviDemandeInterventionState, RootState> = {
    async [DemandeInterventionActions.GET_DEMANDES_INTERVENTION](
        { commit }: DemandeInterventionActionTypes,
        payload: FilterDemandeIntervention
    ) {
        if (abortController) {
            abortController.abort();
        }
        abortController = new AbortController();

        commit(DEMANDE_INTERVENTION.SET_ERROR, undefined);
        commit(DEMANDE_INTERVENTION.SET_IS_LOADING, true);
        commit(DEMANDE_INTERVENTION.SET_PAGE, payload.page);

        const { isBim, ...serviceParams } = payload;

        if (isBim) {
            commit(`equipementState/${EQUIPEMENT.HISTORIQUE_DEMANDE_INTERVENTION_SET_ERROR}`, undefined, {
                root: true,
            });
            commit(`equipementState/${EQUIPEMENT.HISTORIQUE_DEMANDE_INTERVENTION_SET_IS_LOADING}`, true, {
                root: true,
            });
            commit(`equipementState/${EQUIPEMENT.HISTORIQUE_DEMANDE_INTERVENTION_SET_PAGE}`, payload.page, {
                root: true,
            });
        }

        try {
            const response = await DemandeInterventionService.getDemandesIntervention(
                serviceParams,
                abortController.signal
            );
            if (response.status === 200 && response.data !== undefined) {
                if (payload.page === 0) {
                    commit(DEMANDE_INTERVENTION.SET_ITEMS, response.data);
                    commit(DEMANDE_INTERVENTION.SET_SCROLL, 0);
                    commit(DEMANDE_INTERVENTION.SET_NB_OF_PAGES, response.headers.pages);
                    commit(DEMANDE_INTERVENTION.SET_COUNT, response.headers.count);
                    if (isBim) {
                        commit(
                            `equipementState/${EQUIPEMENT.HISTORIQUE_DEMANDE_INTERVENTION_SET_ITEMS}`,
                            response.data,
                            { root: true }
                        );
                        commit(
                            `equipementState/${EQUIPEMENT.HISTORIQUE_DEMANDE_INTERVENTION_SET_NB_OF_PAGES}`,
                            response.headers.pages,
                            {
                                root: true,
                            }
                        );
                        commit(
                            `equipementState/${EQUIPEMENT.HISTORIQUE_DEMANDE_INTERVENTION_SET_COUNT}`,
                            response.headers.count,
                            { root: true }
                        );
                    }
                } else {
                    commit(DEMANDE_INTERVENTION.ADD_ITEMS, response.data);
                    if (isBim) {
                        commit(
                            `equipementState/${EQUIPEMENT.HISTORIQUE_DEMANDE_INTERVENTION_ADD_ITEMS}`,
                            response.data,
                            { root: true }
                        );
                    }
                }
            } else {
                commit(
                    `errorToaster/${ERROR.SET_MESSAGE}`,
                    await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                    { root: true }
                );
            }
            commit(DEMANDE_INTERVENTION.SET_IS_LOADING, false);
            if (isBim) {
                commit(`equipementState/${EQUIPEMENT.HISTORIQUE_DEMANDE_INTERVENTION_SET_IS_LOADING}`, false, {
                    root: true,
                });
            }
        } catch (error) {
            if (axios.isCancel(error)) {
                return;
            }
            commit(DEMANDE_INTERVENTION.SET_IS_LOADING, false);
            commit(DEMANDE_INTERVENTION.SET_ERROR, error);
            commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
            if (isBim) {
                commit(`equipementState/${EQUIPEMENT.HISTORIQUE_DEMANDE_INTERVENTION_SET_IS_LOADING}`, false, {
                    root: true,
                });
                commit(`equipementState/${EQUIPEMENT.HISTORIQUE_DEMANDE_INTERVENTION_SET_ERROR}`, error, {
                    root: true,
                });
            }
        }
    },
    async [DemandeInterventionActions.NAV_FILTER]({ commit }: DemandeInterventionActionTypes, filter: NavFilter) {
        commit(DEMANDE_INTERVENTION.SET_FILTER_STATUS, filter.status);
        commit(DEMANDE_INTERVENTION.SET_FILTER_SITE, filter.sites);
        commit(DEMANDE_INTERVENTION.RESET_ITEMS);
    },
    async [DemandeInterventionActions.GET_ATTACHMENT]({ commit }: DemandeInterventionActionTypes, payload: any) {
        try {
            commit(DEMANDE_INTERVENTION.SET_ATTACHMENT_LOADING, true);
            const response = await DemandeInterventionService.getPieceJointeById(payload);
            if (response.status === 200 && response.data !== undefined) {
                commit(DEMANDE_INTERVENTION.SET_ATTACHMENT_LOADING, false);
                const contentType = response.headers['content-type'];
                if (contentType.includes('image')) {
                    commit(DEMANDE_INTERVENTION.SET_ATTACHMENT, response.data);
                    commit(DEMANDE_INTERVENTION.SET_ATTACHMENT_TYPE, contentType);
                } else if (contentType === 'application/pdf') {
                    window.open(URL.createObjectURL(new Blob([response.data], { type: contentType })));
                }
            } else {
                commit(DEMANDE_INTERVENTION.SET_ATTACHMENT_LOADING, false);
                commit(
                    `errorToaster/${ERROR.SET_MESSAGE}`,
                    await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                    { root: true }
                );
            }
        } catch (error) {
            commit(DEMANDE_INTERVENTION.SET_ATTACHMENT_LOADING, false);
            commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
        }
    },

    async [DemandeInterventionActions.EXPORT_XLS](
        { commit }: DemandeInterventionActionTypes,
        payload: FilterDemandeIntervention
    ) {
        const { isBim, ...serviceParams } = payload;

        if (isBim) {
            commit(`equipementState/${EQUIPEMENT.HISTORIQUE_DEMANDE_INTERVENTION_SET_EXPORT_XLS_IS_LOADING}`, true, {
                root: true,
            });
        } else {
            commit(DEMANDE_INTERVENTION.SET_EXPORT_XLS_IS_LOADING, true);
        }

        try {
            const response = await DemandeInterventionService.getExportXlsDemandesIntervention(serviceParams);
            if (response.status === 200 && response.data !== undefined) {
                const blob = new Blob([response.data], { type: response.headers['content-type'] });
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = 'export_demandes_interventions.xlsx';
                link.click();
                URL.revokeObjectURL(link.href);
            } else {
                commit(
                    `errorToaster/${ERROR.SET_MESSAGE}`,
                    await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                    { root: true }
                );
            }
            if (isBim) {
                commit(
                    `equipementState/${EQUIPEMENT.HISTORIQUE_DEMANDE_INTERVENTION_SET_EXPORT_XLS_IS_LOADING}`,
                    false,
                    {
                        root: true,
                    }
                );
            } else {
                commit(DEMANDE_INTERVENTION.SET_EXPORT_XLS_IS_LOADING, false);
            }
        } catch (error) {
            if (isBim) {
                commit(`equipementState/${EQUIPEMENT.HISTORIQUE_TRAVAUX_SET_EXPORT_XLS_IS_LOADING}`, false, {
                    root: true,
                });
            } else {
                commit(DEMANDE_INTERVENTION.SET_EXPORT_XLS_IS_LOADING, false);
            }
            commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
        }
    },

    async [DemandeInterventionActions.SEND_ASK_INTERVENTION_REMINDER](
        { commit }: DemandeInterventionActionTypes,
        id: number
    ) {
        try {
            const response = await DemandeInterventionService.sendAskInterventionReminder(id);
            if (response.status === 200) {
                // show success toaster
                commit('successToaster/SUCCESS_SET_ACTIVE', true, { root: true });
                setTimeout(() => commit('successToaster/SUCCESS_SET_ACTIVE', false, { root: true }), 5000);
                return true;
            } else {
                commit(
                    `errorToaster/${ERROR.SET_MESSAGE}`,
                    await ErrorManager.processErrorCustomMessage(response.status, 'app.error.undefined'),
                    { root: true }
                );
                return false;
            }
        } catch (error) {
            commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
            return false;
        }
    },
};
